/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import get, {Environment, getEnvironment, isDevelopmentEnvironment} from '@ion/core/src/environment/environment';
import {contains} from '@ion/core/src/utils/StringUtil';
import {isCustomTheme} from '../useExternalTheme';
import {getSimulatedReseller} from './ResellerDevTools';

export enum Reseller {
  AssistCargo = 'assistcargo',
  RACConnect = 'racconnect',
  BrunoSchillig = 'brunoschillig',
  Ayvens = 'ayvens',
  CAT = 'caterpillar'
};

/**
 * Defines the list of CalAmp exclusion domains which are not be be included
 * when determining if the application is running in Co-Branding (Reseller)
 * mode. By default, iOn Applications are deployed to application specific
 * calamp.com subdomains, such as `ion.calamp.com` and `admin.calamp.com`.
 * Likewise, customer co-branded applications are hosted and deployed to the
 * `calamp.com` domain with customer specific subdomain, such as Assist Cargo,
 * which is available at: `assistcargo.calamp.com`.
 */
const exclusions = ['ion', 'admin'];

/**
 * List of affiliates that should not include the "CalAmp" reference
 * in the application (e.g., branding, footer, or "Powered by" text).
 *
 * Extend this list if additional affiliates need to be excluded from
 * showing CalAmp references in the UI.
 */
export const appWithOutCalAmpReference = [
  Reseller.RACConnect,
  Reseller.BrunoSchillig,
  Reseller.Ayvens,
  Reseller.CAT
];

/**
 * List of affiliates that provide a header-specific logo image variant
 * (`logo-header.png`) in place of the standard logo within the application
 * header, where the standard logo would otherwise render too large.
 *
 * This is an opt-in inclusion list: only the affiliates listed here load the
 * header logo variant; all others continue to use the standard logo.
 *
 * Extend this list if additional affiliates supply a header logo variant.
 */
export const appWithHeaderLogo = [
  Reseller.CAT
];

/**
 * List of affiliates that should NOT display the Help section
 * in the application UI.
 *
 * This is typically used to hide support or documentation
 * entry points for specific reseller-branded applications.
 *
 * Extend this list if more affiliates need to have the Help
 * section disabled.
 */
export const appWithHelpSectionExclusions = [
  Reseller.BrunoSchillig
];

/**
 * A mapping of Sisense Theme IDs per Reseller and Environment.
 *
 * Each reseller can have different theme IDs for different deployment environments.
 * Used to dynamically load the appropriate Sisense dashboard theme configuration.
 */
export const SisenseTheme = {
  [Reseller.RACConnect] : {
    [Environment.qa]  : '68d2ef977063a059bce72e7b',
    [Environment.stg] : '68db05a0629295c03f7acd8d',
    [Environment.us]  : '68df50de73c70155ea886cb3',
    [Environment.eu]  : '68df3d8b67991bfb2e45c8b7'
  },
  [Reseller.BrunoSchillig] : {
    [Environment.qa]  : '68efeef17063a059bce896e1',
    [Environment.stg] : '68f902320caac574731a1c11',
    [Environment.us]  : '690025549609975c45710014',
    [Environment.eu]  : '6900290567991bfb2e4618fa'
  }
};

/**
 * Maps a domain hostname to a Reseller.
 *
 * Used to determine which reseller-specific configuration should be applied
 * based on the URL the application is running on.
 *
 * Example:
 *  'connected.rac.co.uk' → Reseller.RACConnect
 */
export const ResellerDomainMap = {
  'connected.rac' : Reseller.RACConnect
};

/**
 * Resolves the Reseller from a given URL or hostname
 * by checking against the ResellerDomainMap.
 *
 * ```typescript
 *  import {getResellerFromDomain} from '@ion/dls/src/theme/resellers/Resellers';
 *
 *  getResellerFromDomain("connected.rac.co.uk")         // → Reseller.RACConnect
 *  getResellerFromDomain("https://connected.rac.co.uk") // → Reseller.RACConnect
 *  getResellerFromDomain("connect.mk.com")              // → undefined
 * ```
 *
 * @param url The incoming URL or hostname used for lookup.
 * @returns The matching Reseller enum entry, or `undefined` if no match found.
 */
export const getResellerFromDomain = (url: string): Reseller | undefined => {
  let hostname = url;
  try {
    hostname = new URL(url).hostname.toLowerCase();
  } catch {
    hostname = url.split('/')[0].toLowerCase();
  }
  for (const key of Object.keys(ResellerDomainMap)) {
    if (hostname.includes(key.toLowerCase())) {
      return ResellerDomainMap[key];
    }
  }
};

/**
 * Resolves the Sisense theme ID for the current reseller and environment.
 *
 * If the application is running under a reseller (as determined by `isReseller()`),
 * this function attempts to retrieve the correct theme ID from the `SisenseTheme` map
 * using the reseller name and current environment. If no match is found, the provided
 * `defaultTheme` is returned.
 *
 * ```typesript
 *
 *  import {getSisenseTheme} from '@ion/dls/src/theme/resellers/Resellers';
 *
 *  const theme = getSisenseTheme('default-theme-id');
 * // Returns a reseller-specific theme ID or 'default-theme-id' as fallback
 *
 * ```
 * @param defaultTheme - The fallback theme ID to use if no reseller-specific theme is found.
 * @returns The matching Sisense theme ID for the reseller/environment, or `defaultTheme`
 *          if none is found.
 */
export const getSisenseTheme = (defaultTheme: string) => (
  isReseller() ? (SisenseTheme[getResellerName()]?.[getEnvironment(get('ctcURL'))]) ?? defaultTheme : defaultTheme
);

/**
 * Provides a global utility which determines if the application is running
 * as a CalAmp Co-Branding instance (Reseller).
 *
 * The logic to determine if the application is a Reseller instance is based
 * on the application being deployed to CalAmp Customer specific domain, and
 * an external `theme.json` document has been loaded.
 *
 * Note: As mentioned above, the `isReseller` API has a direct dependency on
 * the value of `isCustomTheme` (which will be set to `true` if an external
 * `theme.json` document has been loaded). This dependency is necessary as
 * applications pass a list of domain exclusions (such as `qa.ion.calamp.com`,
 * etc.) to the `useExternalTheme` hook, which must be taken into account;
 * however, the `isReseller` API does not maintain state and thus will not
 * have a reference to the domains which should be excluded. The optional
 * `includeCustomTheme` parameter can be set to `false` should clients
 * which to not take the domain exclusions into account during validation.
 *
 * When running in local development mode, the domain check is based on the
 * application being deployed to the localhost loopback address (127.0.0.1),
 * which is the default address used when testing co-branded resellers locally.
 *
 * ```typescript
 *   import isReseller from '@ion/dls/src/resellers';
 *   ...
 *   if (isReseller()) {
 *     // reseller specific implementation ...
 *   }
 *   // without domain exclusions included ...
 *   if (isReseller(false)) {
 *     // reseller specific implementations  ...
 *   }
 * ```
 *
 * @param   includeCustomTheme When `true` (default), will require a call to the
 *          `isCustomTheme` function to return true in addition to the internal
 *          domain validation. If false, only internal domain validation will be
 *          performed.
 * @returns `true` if the application is running as a CalAmp Co-Branding instance
 *          (Reseller), otherwise, `false`.
 */
export const isReseller = (includeCustomTheme = true): boolean => {
  const isValidDomain = Boolean(getResellerDomain());
  if (includeCustomTheme) {
    return isValidDomain && isCustomTheme();
  }
  return isValidDomain;
};

/**
 * @private
 */
let resellerHostCache: {hostname: string; isResellerHost: boolean};

/**
 * Resolves the deployed reseller host check once per hostname.
 *
 * @private
 */
const getCachedResellerHost = (hostname: string): boolean => {
  if (resellerHostCache?.hostname !== hostname) {
    resellerHostCache = {hostname, isResellerHost: isReseller(false)};
  }
  return resellerHostCache.isResellerHost;
};

/**
 * Determines if the application is being served from a reseller host. This is
 * the single predicate used to keep resellers out of dark mode, covering both
 * the dark mode toggle and the application opt-in.
 *
 * Unlike `isReseller`, the check never waits on the asynchronous custom theme
 * flag, so it is safe to call before the external theme has resolved. In
 * deployed environments it is a domain-only check. When running in local
 * development (`localhost`, loopback, LAN, `*.local`), every host would
 * otherwise look like a reseller domain, so only a simulated reseller
 * (`?reseller=<customer>`) counts.
 *
 * The deployed result depends only on the hostname, so it is cached per
 * `location.hostname` and resolved again when the hostname changes. The
 * local development result, and any call made without a `location`, such as
 * during server-side rendering, is never cached.
 *
 * ```typescript
 *   import {isResellerHost} from '@ion/dls/src/theme/resellers/Resellers';
 *   ...
 *   if (!isResellerHost()) {
 *     // enable dark mode ...
 *   }
 * ```
 *
 * @returns `true` if the host is a reseller domain, or a simulated reseller
 *          when running in local development, otherwise, `false`.
 */
export const isResellerHost = (): boolean => {
  const hostname = globalThis?.location?.hostname;
  let isHost: boolean;
  if (isDevelopmentEnvironment()) {
    isHost = Boolean(getSimulatedReseller());
  } else if (hostname) {
    isHost = getCachedResellerHost(hostname);
  } else {
    isHost = isReseller(false);
  }
  return isHost;
};

/**
 * Provides a utility which returns the name of the CalAmp Co-Branding (Reseller)
 * Customer, otherwise, returns `undefined`. When running locally, naturally the
 * reseller domain will not be accessible from localhost, thus the `reseller`
 * querystring parameter will be used, if provided.
 *
 * Note: The function has a direct dependency on the value of `isCustomTheme`
 * (which will be set to `true` if an external`theme.json` document has been
 * loaded). This dependency is necessary as applications pass a list of domain
 * exclusions (such as `qa.ion.calamp.com`, etc.) to the `useExternalTheme`
 * hook, which must be taken into account; however, the `isReseller` API does
 * not maintain state and thus will not have a reference to the domains which
 * should be excluded. The optional `includeCustomTheme` parameter can be set
 * to `false` should clients which to not take the domain exclusions into account
 * during validation.
 *
 * ```typescript
 *   import {getResellerName} from '@ion/dls/src/resellers';
 *   ...
 *
 *   // example production url: https://assistcargo.calamp.com
 *   const customer = getResellerName(); // "assistcargo"
 *
 *   // example pre-production url: https://dev-assistcargo.calamp.com
 *   const customer = getResellerName(); // "assistcargo"
 *
 *   ...
 *   // example local url: http://127.0.0.1:8080?reseller=assistcargo
 *   const customer = getResellerName(); // "assistcargo"
 * ```
 *
 * @param   includeCustomTheme When `true` (default), will require a call to the
 *          `isCustomTheme` function to return true in addition to the internal
 *          domain validation. If false, only internal domain validation will be
 *          performed.
 * @returns The CalAmp Co-Branding (Reseller) customer name based on the deployed
 *          sub-domain environment, otherwise, `undefined`.
 */
export const getResellerName = (includeCustomTheme = true): string => {
  if (!includeCustomTheme || (includeCustomTheme && isCustomTheme())) {
    return normalizeDomainName(getResellerDomain());
  }
};

/**
 * Generic utility to determine whether a feature should be enabled
 * based on the current reseller and an exclusion list.
 *
 * This function retrieves the reseller name using `getResellerName()`
 * and checks whether it exists in the provided exclusion list.
 *
 * ```typescript
 *   const canShow = isFeatureEnabledForReseller(appWithHelpSectionExclusions);
 * ```
 *
 * @param exclusions - List of resellers for which the feature is disabled
 * @param defaultVal - Default value to return when no reseller is set
 * @returns `true` if no reseller is set or the reseller is not in the
 * excluded list, otherwise `false`
 */
export const isFeatureEnabledForReseller = (
  exclusions: Reseller[],
  defaultVal = true
): boolean => {
  const reseller = getResellerName() as Reseller;
  if (!reseller) {
    return defaultVal;
  }
  return !exclusions.includes(reseller);
};

/**
 * Determines whether the "CalAmp" reference should be included
 * for the current reseller.
 *
 * Uses the reseller name returned by `getResellerName()` and checks
 * if it exists in the {@link appWithOutCalAmpReference} exclusion list.
 *
 * ```typescript
 *   const includeRef = shouldIncludeCalAmpReference();
 *   if (includeRef) {
 *     // Render "Powered by CalAmp"
 *   }
 * ```
 *
 * @returns `true` if no reseller is set or the reseller is not in
 * the excluded list, otherwise `false`.
 */
export const shouldIncludeCalAmpReference = (defaultVal = true) => (
  isFeatureEnabledForReseller(appWithOutCalAmpReference, defaultVal)
);

/**
 * Determines whether the current reseller provides a header-specific logo
 * image variant that should be loaded in place of the standard logo within
 * the application header.
 *
 * Uses the reseller name returned by `getResellerName()` and checks if it
 * exists in the {@link appWithHeaderLogo} inclusion list. Unlike the exclusion
 * helpers, this is an opt-in check: it returns `false` when no reseller is set.
 *
 * ```typescript
 *   const useHeaderLogo = shouldUseHeaderLogo();
 *   if (useHeaderLogo) {
 *     // load the reseller header logo variant ...
 *   }
 * ```
 *
 * @returns `true` if the current reseller is in the header logo inclusion
 * list, otherwise `false`.
 */
export const shouldUseHeaderLogo = (): boolean => {
  const reseller = getResellerName() as Reseller;
  return !!reseller && appWithHeaderLogo.includes(reseller);
};

/**
 * Determines whether the Help section should be displayed
 * for the current reseller.
 *
 * Uses the reseller name returned by `getResellerName()` and checks
 * if it exists in the {@link appWithHelpSectionExclusions} exclusion list.
 *
 * ```typescript
 *   const showHelp = showHelpSection();
 *   if (showHelp) {
 *     // Render Help section
 *   }
 * ```
 *
 * @returns `true` if no reseller is set or the reseller is not in
 * the excluded list, otherwise `false`.
 */
export const showHelpSection = () => (
  isFeatureEnabledForReseller(appWithHelpSectionExclusions)
);

/**
 * Retrieves the support details (contact information and social links)
 * for the current reseller.
 *
 * Falls back to the default `calamp` support configuration when
 * no reseller is found or no support entry exists for the reseller.
 *
 * ```typescript
 *   const support = getSupportDetails();
 *   console.log(support.email);  // reseller or default support email
 *   console.log(support.phone);  // reseller or default support phone
 * ```
 *
 * @returns An object containing the following fields:
 * - `email`: Support email address
 * - `phone`: Support phone number
 * - `linkedIn`: LinkedIn page URL
 * - `facebook`: Facebook page URL
 * - `twitter`: Twitter handle URL
 * - `youtube`: YouTube channel URL
 * - `solutionSupport`: Solution support portal URL
 */
export const getSupportDetails = () => {
  const reseller   = getResellerName() as Reseller;
  const support    = get('support');
  const appSupport = support?.[!reseller || !support[reseller] ? 'calamp' : reseller] || {};
  return {
    email           : appSupport.supportEmail,
    phone           : appSupport.supportPhone,
    linkedIn        : appSupport.linkedInUrl,
    facebook        : appSupport.facebookUrl,
    twitter         : appSupport.twitterUrl,
    youtube         : appSupport.youtubeUrl,
    solutionSupport : appSupport.solutionSupportURL
  };
};

/**
 * Provides a utility which returns the filename (excluding extension) for the
 * CalAmp Reseller translation document.
 *
 * By default, all customer reseller translation documents are prefixed with
 * `reseller-`. This default can be overridden via the prefix argument.
 *
 * ```typescript
 *   import {getTranslationsFilename} from '@ion/dls/src/resellers';
 *   ...
 *
 *   // example production url: https://assistcargo.calamp.com
 *   const customer = getTranslationsFilename(); // "reseller-assistcargo"
 *
 *   // example pre-production url: https://dev-assistcargo.calamp.com
 *   const customer = getTranslationsFilename(); // "reseller-assistcargo"
 *
 *   ...
 *   // example local url: http://127.0.0.1:8080?reseller=assistcargo
 *   const customer = getTranslationsFilename(); // "reseller-assistcargo"
 * ```
 *
 * @param   prefix An optional prefix to be appended to the customer translation
 *          document name.
 * @returns The translation document name for the CalAmp Reseller.
 */
export const getTranslationsFilename = (prefix = 'reseller') => {
  if (isReseller(false)) {
    return `${prefix}-${getResellerName(false)}`;
  }
};

/**
 * Provides a convenience function which allows for applying a reseller translation
 * document to the given list of translations to be included.
 *
 * ```typescript
 *   import {applyTranslations} from '@ion/dls/src/theme/resellers/Resellers';
 *
 *   ...
 *   // application is deployed to 'assistcargo' reseller domain ...
 *   const translations = applyTranslations(['login','common']);
 *   translations[0]; // 'login'
 *   translations[1]; // 'common'
 *   translations[0]; // 'reseller-assistcargo'
 *
 *
 *   // application is not deployed to a reseller domain ...
 *   const translations = applyTranslations(['login','common']);
 *   translations[0]; // 'login'
 *   translations[1]; // 'common'
 *
 *   ...
 *   const LoginPage = withTranslation(translations))(Page) as PageLayoutConsumer;
 * ```
 *
 * @param   translations The list of translation modules to be included
 * @param   prefix An optional prefix to be appended to the customer translation
 *          document name.
 * @param   resolver An optional function which resolves the translation file to
 *          be used. This is useful for cases where the reseller translation file
 *          does not contain the host name, but is mapped to an alias for simplicity,
 *          such as `https://some-long-name-reseller.com` can be aliased to simply
 *          `reseller-slnr.json`, etc..
 * @returns the provided list of translations with the given resellers translation
 *          module added when `isReseller` is `true`.
 */
export const applyTranslations = (
  translations: string[],
  prefix = 'reseller',
  resolver = getTranslationsFilename): string[] => {
  if (isReseller(false)) {
    return [...translations, resolver(prefix)];
  }
  return translations;
};

/**
 * Provides a utility which normalizes the reseller domain name by removing any
 * pre-prod environment specific prefixes; e.g. `dev-assistcargo' will be resolved
 * to `assistcargo`, etc..
 */
const normalizeDomainName = (domain: string): string => {
  if (domain) {
    return domain.replace(/(dev|qa|stg)[.-]/g, '');
  }
  return domain;
};

/**
 * Provides a utility which returns the name of the CalAmp Co-Branding (Reseller)
 * Sub-Domain.
 *
 * Note: During local development, the reseller domain is simulated and resolved
 * based on the initial `?reseller=<customer-name>` parameter provided to the
 * application query string.
 *
 * @returns The CalAmp Co-Branding (Reseller) sub-domain based on the deployed
 *          environment.
 */
const getResellerDomain = (): string => {
  const simulated  = getSimulatedReseller();
  const {hostname} = globalThis?.location || {};
  if (hostname && !simulated) {
    const domain = getResellerFromDomain(hostname) ?? normalizeDomainName(hostname.split('.calamp')[0]);
    if (!exclusions.some(exclusion => exclusion === domain)) {
      return domain;
    }
  }
  return simulated;
};

export default isReseller;
